import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import { ICreatedInstanceId, IGcpProjectId } from 'src/app/utils/IFactory';
import {
    DatasourceColumnId,
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    generateTableFullPath,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { PromiseCancellationToken } from 'src/app/utils/promise/cancellation-token';
import { buildWhereSQL } from 'src/app/utils/query/sql';
import { hashCode } from 'src/app/utils/string-hash';
import {
    DataLoaderResult,
    IDataLoaderResult,
    mergeDataLoaderResultStatus,
} from '../../../services/data-loader/data-loader-result';
import { standardRun } from '../../../services/data-loader/utils/standard-run';
import { DatasourceColumnHelper } from './datasource-column-helper';

export interface DatasourceColumnDataLoaderFactoryOptions
    extends ICreatedInstanceId {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId | (() => GcpProjectId);
    queryCacheService: QueryCacheService;
    datasourceColumnId: DatasourceColumnId;
    datasourceSqlMap: IDatasourceSQLMap | IDatasourceSQLMap[];
    /** Add where statement based on queryContext. Default false. One can also pass an array of columnIds to limit the context filter */
    filterContext?: boolean | DatasourceColumnId[];
    /** Ignore columns from queryContext
     * @warning If filterContext is set as an array, then ignoredContextColumnIds values are ignored
     */
    ignoredContextColumnIds?: DatasourceColumnId[];
    /** Order by query */
    orderByQuery?: string | undefined;
    /** Use this option if you want to add additional sql token */
    additionalSQLTokens?: Partial<ICompiledSQLTokens>;
    /** Dev mode, for testing purposes only */
    devMode?: {
        /** Wait time in ms before executing the query */
        wait?: number;
    };
}

export class DataSourceColumnDataLoaderFactory extends Factory<
    DataLoader,
    DatasourceColumnDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'DataSourceColumnDataLoaderFactory';

    override getInstance(
        options: DatasourceColumnDataLoaderFactoryOptions
    ): DataLoader {
        // resolve default options
        const finalOptions = {
            ...options,
            filterContext: options.filterContext ?? false,
            ignoredContextColumnIds: options.ignoredContextColumnIds ?? [
                'ACTIVITY_APP_VERSION',
            ],
            orderByQuery: options.orderByQuery ?? 'value',
            additionalSQLTokens: options.additionalSQLTokens ?? {},
            devMode: options.devMode ?? {},
        };

        return new DataLoader(finalOptions.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            await DataSourceColumnDataLoaderFactory.executeQueryContext(
                this,
                queryContext,
                cancellationToken,
                finalOptions
            );
        });
    }

    private static async executeQueryContext(
        instance: DataLoader,
        queryContext: IQueryContext,
        cancellationToken: PromiseCancellationToken,
        options: Required<DatasourceColumnDataLoaderFactoryOptions>
    ) {
        // Resolve gcpProjectId to always be a value
        const resolvedGcpProjectId: GcpProjectId =
            typeof options.gcpProjectId === 'function'
                ? options.gcpProjectId()
                : options.gcpProjectId;

        const datasourceColumnId: DatasourceColumnId =
            options.datasourceColumnId;

        // Helper to build a SELECT query for a given sqlMap
        const buildSelectQuery = (datasourceSqlMap: IDatasourceSQLMap) => {
            // identify columns not supported in datasource
            const dsColumns = DataLoader.separatePresentAndMissingDims(
                queryContext.datasourceColumns,
                datasourceSqlMap.columns
            );

            const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                DataLoader.getMissingColumnsStatus(dsColumns.missing);

            const sqlTokensArray: Partial<ICompiledSQLTokens>[] =
                dsColumns.present
                    // filter out queried column, because we don't want to filter by itself
                    .filter(
                        (col: IQueryContextDatasourceColumn) =>
                            col.id != datasourceColumnId
                    )
                    // context filter functionality
                    .filter((col: IQueryContextDatasourceColumn) =>
                        DatasourceColumnHelper.filterContextFn(col, {
                            filterContext: options.filterContext,
                            ignoredContextColumnIds:
                                options.ignoredContextColumnIds,
                        })
                    )
                    .map((dsCol) => {
                        return datasourceSqlMap.columns[dsCol.id]!
                            .compileSQLTokens!(dsCol.state, {
                            gcpProjectId: resolvedGcpProjectId,
                        });
                    });

            // push additional SQLTokens
            sqlTokensArray.push(options.additionalSQLTokens);

            // merge tokens into single deduplicated object of type ICompiledSQLTokens
            const sqlTokens: ICompiledSQLTokens =
                mergeSqlTokensArray(sqlTokensArray);

            const columnSelect =
                datasourceSqlMap.columns[datasourceColumnId]!.columnSQL;
            const columnAlias = datasourceColumnId.replace('.', '_');
            const unnestDatasourceColumn =
                sqlTokens.unnestDatasourceColumn.join('\n');
            const where: string = buildWhereSQL(sqlTokens.where);
            const join: string = sqlTokens.join.join('\n');

            return {
                query: `
SELECT DISTINCT ${columnSelect} AS value, COALESCE(CAST(${columnSelect} AS STRING), 'N/A') AS label
FROM \`${generateTableFullPath(
                    resolvedGcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                )}\` ds
${unnestDatasourceColumn}
${join}
${where}
ORDER BY ${options.orderByQuery}
        `.trim(),

                pendingDataLoaderResultStatus,
                pendingStatusMessage,
            };
        };

        const process = async () => {
            instance.emitter.emit(
                DataLoaderResult.getLoading(instance.latestData, queryContext)
            );
            if (options.devMode.wait) {
                await new Promise((resolve) =>
                    setTimeout(resolve, options.devMode.wait)
                );
            }

            // Support array or single object for datasourceSqlMap
            const sqlMaps = Array.isArray(options.datasourceSqlMap)
                ? options.datasourceSqlMap
                : [options.datasourceSqlMap];

            // Build queries for each map
            const queries = sqlMaps.map(buildSelectQuery);
            // if only one query, return it directly
            const finalQuery = (() => {
                if (queries.length === 1) {
                    return `
-- DataLoaderId : ${instance.instanceId}
${queries[0].query}`;
                }
                // Union all queries
                const unionQuery = queries
                    .map((q) => `(${q.query})`)
                    .join('\nUNION ALL\n');

                // Final query: select distinct from union
                return `
-- DataLoaderId : ${instance.instanceId}
SELECT DISTINCT value, label
FROM (
${unionQuery}
) AS unioned
ORDER BY ${options.orderByQuery}
        `.trim();
            })();

            const queryHash = hashCode(finalQuery);
            if (instance.latestQueryHash !== undefined) {
                if (instance.latestQueryHash === queryHash) return;
                // cancel only if we have data from previous query
                cancellationToken.throwIfCancellationRequested();
            }

            const queryResponse = await DataLoader.runQuery(
                finalQuery,
                options.queryCacheService
            );
            const data = [...queryResponse.result];
            instance.latestQueryHash = queryHash;
            instance.latestData = structuredClone(data);

            let dl: IDataLoaderResult = {
                data,
                queryContext,
                status: mergeDataLoaderResultStatus(
                    queries.map((q) => q.pendingDataLoaderResultStatus)
                ),
                statusMessage: queries
                    .map((q) => q.pendingStatusMessage)
                    .join('\n'),
                isLoading: false,
                sqlAndJsProcessing: finalQuery,
            };
            instance.emitter.emit(dl);
        };

        await standardRun(instance, queryContext, process);
    }
}
